/**
 * Pure conflict-day detection for the availability month view (the desktop
 * corner pip + its popover). Bucketing is on a LOCAL day basis via
 * lib/days.bucketEventsByLocalDay, so it matches BOTH the browser-local
 * react-big-calendar grid AND the mobile agenda — the same primitive feeds all
 * three. A "conflict day" is one where a non-hidden conflict-calendar event
 * time-overlaps a POSITIVE availability signal (free-time calendar event,
 * finalized session, or legacy busyWindow). A user's own authored mark is
 * deliberately NOT positive (the pip warns about free calendar time / sessions
 * landing on a busy block, not the marks you intentionally authored).
 *
 * Hidden conflict events do NOT contribute to the overlap signal — they render
 * dimmed and explicitly "won't block" anything per design §5.5.
 *
 * Inputs are react-big-calendar event objects (as produced by
 * buildAvailabilityCalendarEvents): { start, end, allDay?, resource: {...} }.
 */

import { bucketEventsByLocalDay, localDayBounds } from "../../../lib/days";
import { intervalsOverlap } from "../../../lib/intervals";
import { coerceDate } from "../../../lib/time";

// Thin object-shaped adapter over the canonical lib/intervals predicate. Kept as
// a named export because dayHasConflict (and its tests) read more clearly with
// event objects; the half-open / degenerate / coercion rules live in one place.
export function timesOverlap(a, b) {
  return intervalsOverlap(a?.start, a?.end, b?.start, b?.end);
}

// A usable interval has a real positive duration. A degenerate (zero-length or
// inverted) event never contributes a conflict — without this guard a point
// event sitting inside a positive window would report a spurious overlap.
export function isValidInterval(e) {
  const s = coerceDate(e?.start);
  const en = coerceDate(e?.end);
  return !!s && !!en && en.getTime() > s.getTime();
}

// A "conflict" event: a non-hidden linked-calendar event on a conflict-type
// calendar.
export function isConflictEvent(e) {
  return (
    e?.resource?.kind === "external" &&
    e?.resource?.calendarType === "conflict" &&
    !e?.resource?.hidden
  );
}

// A "positive" availability signal: a free-time linked-calendar event, a
// finalized session, or a (legacy) busyWindow. NOTE: `busyWindow` is currently
// emitted by no production path — it is kept only so both surfaces classify
// identically (back-compat); a candidate for removal in the calendar audit.
export function isPositiveEvent(e) {
  const kind = e?.resource?.kind;
  return (
    (kind === "external" && e?.resource?.calendarType === "free") ||
    kind === "finalizedSession" ||
    kind === "busyWindow"
  );
}

// THE canonical per-day conflict predicate, shared by the desktop month-view
// detector (below) and the mobile agenda (MobileAvailabilityAgenda imports it).
// A day is in conflict when a non-hidden conflict event time-overlaps a positive
// signal among the valid-interval events bucketed to that day.
export function dayHasConflict(dayEvents = []) {
  const valid = dayEvents.filter(isValidInterval);
  const conflicts = valid.filter(isConflictEvent);
  if (conflicts.length === 0) return false;
  const positives = valid.filter(isPositiveEvent);
  if (positives.length === 0) return false;
  return conflicts.some((c) => positives.some((p) => timesOverlap(c, p)));
}

/**
 * The set of LOCAL day-keys (yyyy-MM-dd in `timeZone`, default browser) where a
 * conflict event overlaps a positive availability signal. Events are bucketed
 * by the local days they touch, so a multi-day / cross-midnight conflict is
 * evaluated on EVERY day it spans — matching the browser-local RBC grid and the
 * mobile agenda. (The old UTC start-day bucketing put the pip on the wrong cell
 * for non-UTC users and missed conflicts on day 2+ of a span.)
 *
 * @param {Array} events
 * @param {object} [opts]
 * @param {string} [opts.timeZone]    day-boundary zone (defaults to browser).
 * @param {Date|string|number} [opts.windowStart] optional inclusive clamp.
 * @param {Date|string|number} [opts.windowEnd]   optional exclusive clamp.
 * @returns {Set<string>}
 */
export function detectConflictDays(
  events = [],
  { timeZone, windowStart, windowEnd } = {}
) {
  const byDay = bucketEventsByLocalDay({
    events,
    timeZone,
    windowStart,
    windowEnd,
  });
  const conflictDays = new Set();
  for (const [key, group] of byDay) {
    if (dayHasConflict(group.rows.map((r) => r.event))) {
      conflictDays.add(key);
    }
  }
  return conflictDays;
}

/**
 * The rows the conflict popover renders for a single local day: every event
 * overlapping that day, CLIPPED to the day and flagged as a continuation when
 * it began earlier — so the popover shows day-relative times (mirroring the
 * mobile agenda) instead of a raw multi-day range. Sorted by clipped start.
 *
 * @param {Date} date    the cell's day (browser-local midnight from RBC).
 * @param {Array} events
 * @param {object} [opts]
 * @param {string} [opts.timeZone]  day-boundary zone (defaults to browser).
 * @returns {{event, clippedStart: Date, clippedEnd: Date, isContinuation: boolean, isAllDay: boolean, dayKey: string}[]}
 */
export function buildDayTimeline(date, events = [], { timeZone } = {}) {
  const bounds = localDayBounds(date, timeZone);
  if (!bounds) return [];
  const byDay = bucketEventsByLocalDay({
    events,
    timeZone,
    windowStart: bounds.dayStart,
    windowEnd: bounds.dayEnd,
  });
  const group = byDay.get(bounds.dayKey);
  if (!group) return [];
  return group.rows
    .slice()
    .sort((a, b) => a.clippedStart.getTime() - b.clippedStart.getTime());
}
